import { NavLink, Navigate } from 'react-router-dom'
import {
  isCurrentUserAdminOrGlobalAdminSelector,
  isCurrentUserAdminSelector
} from '@axilon-apps/domains-identity/state'
import { UsersPage as IdentityUsersPage } from '@axilon-apps/domains-identity/features/users'
import { useAppSelector } from '@axilon-apps/domains-ontology-ui-state'

import { Tabs, TabsList } from '../../components/ui/tabs'
import { GroupsTab } from './groups/GroupsTab'
import { RoleAssignmentsTab } from './RoleAssignmentsTab'

export type UsersSection = 'users' | 'role-assignments' | 'groups'

export const USERS_SECTIONS: ReadonlyArray<{
  value: UsersSection
  label: string
  href: string
}> = [
  { value: 'users', label: 'Users', href: '/users' },
  { value: 'role-assignments', label: 'Role Assignments', href: '/users/role-assignments' },
  { value: 'groups', label: 'Groups', href: '/users/groups' }
]

/** Admins can manage users, groups, and non-global-admin role assignments. */
export function visibleUsersSections(canManageGroups: boolean) {
  return canManageGroups ? USERS_SECTIONS : USERS_SECTIONS.filter((item) => item.value !== 'groups')
}

export function UsersPage({ section }: { section: UsersSection }) {
  const isGlobalAdmin = useAppSelector(isCurrentUserAdminSelector)
  const canManageUsers = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  if (!canManageUsers) return <Navigate to="/plant" replace />

  const sections = visibleUsersSections(canManageUsers)
  if (!sections.some((item) => item.value === section)) {
    return <Navigate to="/users" replace />
  }

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-3.5">
      <header>
        <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Users</h1>
        <p className="mt-1 text-13 text-ink-3">
          {isGlobalAdmin
            ? 'Manage users, role assignments, and groups.'
            : 'Manage users, passwords, account status, role assignments, and groups.'}
        </p>
      </header>

      {sections.length > 1 ? (
        <Tabs value={section} className="gap-0">
          <TabsList
            variant="line"
            className="h-auto w-full justify-start gap-1 rounded-none border-b border-border bg-transparent p-0"
          >
            {sections.map((item) => (
              <NavLink
                key={item.value}
                to={item.href}
                className={({ isActive }) =>
                  [
                    'relative inline-flex h-auto items-center justify-center gap-1.5 rounded-none px-2.5 pb-2.5 pt-1.5 text-13 font-medium text-ink-3 transition-colors after:absolute after:inset-x-0 after:bottom-[-1px] after:h-0.5',
                    isActive ? 'text-ink after:bg-brand' : 'hover:text-ink after:bg-transparent'
                  ].join(' ')
                }
                end={item.value === 'users'}
              >
                {item.label}
              </NavLink>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      {section === 'users' && <IdentityUsersPage inlineFilters />}
      {section === 'role-assignments' && <RoleAssignmentsTab isGlobalAdmin={isGlobalAdmin} />}
      {section === 'groups' && <GroupsTab />}
    </div>
  )
}
